import { VideoProject } from "@/services/bedrock";

export interface QaFlag {
  id: string;
  category: VideoProject.QaIssueCategory;
  comment: string;
  timestampMs?: number | null;
}

export function createQaFlagId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `qa-flag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function buildQaIssues(flags: QaFlag[]): VideoProject.QaIssue[] {
  return flags.map(flag => {
    const comment = flag.comment.trim();
    return {
      category: flag.category,
      comment: comment || null,
      timestampMs: flag.timestampMs ?? null,
    };
  });
}

export function formatQaTimestamp(timestampMs?: number | null): string | null {
  if (timestampMs == null || Number.isNaN(timestampMs)) return null;
  const totalSeconds = Math.max(0, Math.floor(timestampMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export const QA_TIMESTAMP_NUDGE_MS = 1000;

export function clampQaTimestampMs(timestampMs: number, durationSeconds: number): number {
  if (!Number.isFinite(timestampMs)) return 0;
  const rounded = Math.max(0, Math.round(timestampMs));
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return rounded;
  return Math.min(rounded, Math.round(durationSeconds * 1000));
}

export function timestampMsFromTimelineX(
  clientX: number,
  rect: Pick<DOMRect, "left" | "width">,
  durationSeconds: number
): number {
  if (!rect.width) return 0;
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  return clampQaTimestampMs(ratio * durationSeconds * 1000, durationSeconds);
}

export function nudgeQaTimestampMs(
  timestampMs: number | null | undefined,
  deltaMs: number,
  durationSeconds: number
): number {
  return clampQaTimestampMs((timestampMs ?? 0) + deltaMs, durationSeconds);
}

export function parseQaTimestamp(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const clock = trimmed.match(/^(\d+):([0-5]?\d)$/);
  if (clock) return (Number(clock[1]) * 60 + Number(clock[2])) * 1000;
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  return null;
}

export type LastFailResolution = "RESOLVED" | "UNRESOLVED";

export function lastFailTimestampMs(
  issue: Pick<VideoProject.QaAuditIssue, "id" | "timestampMs">,
  overrides: Record<number, number | null | undefined>
): number | null {
  if (Object.prototype.hasOwnProperty.call(overrides, issue.id)) {
    return overrides[issue.id] ?? null;
  }
  return issue.timestampMs ?? null;
}

export function lastFailComment(
  issue: Pick<VideoProject.QaAuditIssue, "id" | "comment">,
  overrides: Record<number, string | undefined>
): string {
  return overrides[issue.id] ?? issue.comment ?? "";
}

export function unmarkedLastFailIssues(
  issues: VideoProject.QaAuditIssue[] | undefined,
  resolutions: Record<number, LastFailResolution | undefined>
): VideoProject.QaAuditIssue[] {
  return (issues || []).filter(issue => resolutions[issue.id] == null);
}

export function unresolvedLastFailIssues(
  issues: VideoProject.QaAuditIssue[] | undefined,
  resolutions: Record<number, LastFailResolution | undefined>,
  timestampOverrides: Record<number, number | null | undefined> = {},
  commentOverrides: Record<number, string | undefined> = {}
): VideoProject.QaIssue[] {
  return (issues || [])
    .filter(issue => resolutions[issue.id] === "UNRESOLVED")
    .map(issue => ({
      category: issue.category,
      comment: lastFailComment(issue, commentOverrides).trim() || null,
      timestampMs: lastFailTimestampMs(issue, timestampOverrides),
    }));
}
